import { useEffect } from "react";
import { useState } from "react";
import SEO from "../components/SEO";
import { Calendar, Menu, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";
const CALENDLY_URL = "https://calendly.com/cityworth/discovery-call";

const BookACallPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="book-a-call-page">
      <SEO
        title="Book a Call"
        description="Schedule a discovery call with CityWorth to learn how homeownership benefits can reduce turnover and retain your best employees."
      />

      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? "rotate-180" : ""}`} />
              </Button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {menuItems.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => { item.action(); setIsMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container-main text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-6">
            <Calendar className="w-4 h-4 text-red-600" />
            <span className="text-sm font-semibold text-red-700">Discovery Call</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" data-testid="book-a-call-heading">
            Slash Employee Turnover and <span className="gradient-text">Supercharge Profits</span>
          </h1>
          <p className="text-lg text-slate-600">
            Book a 30 minute call to see how our Homeownership Benefit will greatly reduce turnover and increase your profits.
          </p>
        </div>
      </section>

      {/* Calendly Embed */}
      <section className="py-12">
        <div className="container-main max-w-4xl mx-auto">
          <div
            className="calendly-inline-widget rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white"
            data-url={CALENDLY_URL}
            data-testid="calendly-embed"
            style={{ minWidth: "320px", height: "700px" }}
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</Link>
            <Link to="/retention-programs" className="text-slate-600 hover:text-slate-900">Programs</Link>
            <Link to="/our-story" className="text-slate-600 hover:text-slate-900">Our Story</Link>
            <Link to="/retention-calculator" className="text-slate-600 hover:text-slate-900">Calculator</Link>
            <Link to="/book-a-call" className="text-slate-600 hover:text-slate-900">Book a Call</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
          </div>
          <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </div>
  );
};

export default BookACallPage;
